import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Factory, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FileUpload } from "../FileUpload";
import { authApi, formatApiErrorDetail } from "../../lib/authApi";
import { OrderStatusBadge, StageTrack, OrderParts, ProgressList, OrderTimeline, fmtDate } from "./OrderShared";
import { QualitySummary } from "./QualityDeliverySummary";
import { OrderDocuments } from "./OrderDocuments";

const ProgressForm = ({ order, onSaved }) => {
  const [stage, setStage] = useState("IN_PROGRESS");
  const [percent, setPercent] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const send = async () => {
    const pct = parseInt(percent, 10);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error("Enter progress between 0 and 100.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await authApi.post(`/partner/orders/${order.id}/progress`, { stage, percent: pct, note, file_ids: files.map((f) => f.id) });
      toast.success(stage === "COMPLETED" ? "Production marked complete." : "Progress update sent.");
      setPercent(""); setNote(""); setFiles([]);
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 p-5 rounded-lg bg-muted/40 border border-border space-y-4" data-testid={`partner-order-progress-form-${order.id}`}>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Stage</Label>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger data-testid={`partner-progress-stage-${order.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="STARTED" data-testid={`partner-progress-stage-started-${order.id}`}>Started</SelectItem>
              <SelectItem value="IN_PROGRESS" data-testid={`partner-progress-stage-in-progress-${order.id}`}>In progress</SelectItem>
              <SelectItem value="COMPLETED" data-testid={`partner-progress-stage-completed-${order.id}`}>Completed (100%)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Progress %</Label>
          <Input type="number" min="0" max="100" value={percent} onChange={(e) => setPercent(e.target.value)} data-testid={`partner-progress-percent-${order.id}`} />
        </div>
        <div className="space-y-2 sm:col-span-3">
          <Label>Note</Label>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Machining done, awaiting finishing..." data-testid={`partner-progress-note-${order.id}`} />
        </div>
      </div>
      <FileUpload files={files} onChange={setFiles} maxFiles={10} testId={`partner-progress-upload-${order.id}`} />
      <Button size="sm" disabled={saving} onClick={send} className="rounded-full px-6" data-testid={`partner-progress-send-btn-${order.id}`}>
        {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Send update
      </Button>
    </div>
  );
};

export const PartnerOrdersSection = ({ orders, setOrders }) => {
  const [acting, setActing] = useState(null);
  const [reasons, setReasons] = useState({});
  const [downloading, setDownloading] = useState(null);

  const upsert = (o) => setOrders((prev) => prev.map((x) => (x.id === o.id ? o : x)));

  const accept = async (o) => {
    setActing(o.id);
    try {
      const { data } = await authApi.post(`/partner/orders/${o.id}/accept`, { note: "" });
      upsert(data);
      toast.success("Order accepted. You can now post production updates.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  const decline = async (o) => {
    const reason = (reasons[o.id] || "").trim();
    if (reason.length < 3) {
      toast.error("Please give a short decline reason.");
      return;
    }
    setActing(o.id);
    try {
      await authApi.post(`/partner/orders/${o.id}/decline`, { reason });
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      toast.success("Order declined. Vectron will reassign it.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  const download = (o) => async (f) => {
    setDownloading(f.id);
    try {
      const res = await authApi.get(`/partner/orders/${o.id}/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed.");
    } finally {
      setDownloading(null);
    }
  };

  if (orders.length === 0) {
    return (
      <div className="p-10 rounded-xl bg-card border border-border text-center" data-testid="partner-orders-empty">
        <Factory className="h-8 w-8 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
        <h3 className="font-semibold font-heading">No work orders assigned</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">Keep your processes and machines up to date — Vectron matches orders to your capabilities.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="partner-orders-section">
      {orders.map((o) => (
        <div key={o.id} className="p-6 rounded-xl bg-card border border-border" data-testid={`partner-order-card-${o.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono-tech text-sm font-bold text-primary" data-testid={`partner-order-number-${o.id}`}>{o.order_number}</span>
              <OrderStatusBadge status={o.status} />
              <span className="text-sm text-muted-foreground">Lead time: <b>{o.lead_time_days ?? "—"} days</b></span>
            </div>
            <span className="text-xs text-muted-foreground">Assigned {o.assignment?.assigned_at ? fmtDate(o.assignment.assigned_at) : ""}</span>
          </div>
          {o.assignment?.note && <p className="mt-2 text-sm text-muted-foreground">Vectron note: {o.assignment.note}</p>}
          <div className="mt-4"><StageTrack status={o.status} heldFrom={o.held_from} /></div>
          {o.status === "ON_HOLD" && <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">On hold by Vectron{o.hold_reason ? `: ${o.hold_reason}` : ""}. Pause production updates.</p>}
          <OrderParts order={o} onDownload={o.status !== "CANCELLED" ? download(o) : null} downloading={downloading} testPrefix="partner-order" />
          <ProgressList order={o} onDownload={download(o)} downloading={downloading} testPrefix="partner-order" />
          <QualitySummary order={o} viewer="partner" onDownload={download(o)} downloading={downloading} />
          <OrderDocuments order={o} role="partner" />
          {o.status === "REWORK_REQUIRED" && <p className="mt-3 text-sm text-orange-600 dark:text-orange-400" data-testid={`partner-order-rework-banner-${o.id}`}>Rework cycle {o.rework_cycle} required — follow the instructions above, post progress, then mark COMPLETED for re-inspection.</p>}

          {o.status === "PARTNER_ASSIGNED" && o.assignment?.status === "PENDING" && (
            <div className="mt-4 p-5 rounded-lg bg-violet-500/5 border border-violet-500/20 space-y-3" data-testid={`partner-order-decision-${o.id}`}>
              <p className="text-sm">Review the parts and drawings, then accept this work order or decline with a reason.</p>
              <Input value={reasons[o.id] || ""} onChange={(e) => setReasons({ ...reasons, [o.id]: e.target.value })} placeholder="Decline reason (capacity, process, lead time...)" className="max-w-md" data-testid={`partner-order-decline-reason-${o.id}`} />
              <div className="flex gap-3">
                <Button size="sm" disabled={acting === o.id} onClick={() => accept(o)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`partner-order-accept-btn-${o.id}`}>
                  <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Accept
                </Button>
                <Button size="sm" variant="outline" disabled={acting === o.id} onClick={() => decline(o)} className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10" data-testid={`partner-order-decline-btn-${o.id}`}>
                  <XCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Decline
                </Button>
              </div>
            </div>
          )}
          {["ACCEPTED", "IN_PRODUCTION", "REWORK_REQUIRED"].includes(o.status) && <ProgressForm order={o} onSaved={upsert} />}
          <OrderTimeline order={o} />
        </div>
      ))}
    </div>
  );
};
